import { ChangeDetectionStrategy, Component, ElementRef, inject, OnInit, signal, viewChild, effect } from '@angular/core';
import { RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Chart, registerables } from 'chart.js';
import { IndicatorsService } from '@modules/indicadores/services/indicators.service';
import { AnalyticsService, TimeSeriesPoint } from '@modules/analytics/services/analytics.service';
import { Indicador } from '@core/models/indicator.model';
import { AuthService } from '@core/services/auth.service';
import { DataTransferService } from '@core/services/data-transfer.service';

Chart.register(...registerables);

import { Button } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { Drawer } from 'primeng/drawer';

@Component({
  selector: 'app-analytics-dashboard',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, RouterLink, Button, TooltipModule, Drawer],
  templateUrl: './analytics-dashboard.html',
  styleUrl: './analytics-dashboard.css',
})
export class AnalyticsDashboard implements OnInit {
  private readonly indicatorsService = inject(IndicatorsService);
  private readonly analyticsService = inject(AnalyticsService);
  protected readonly authService = inject(AuthService);
  protected readonly dataTransferService = inject(DataTransferService);

  protected readonly showMobileMenu = signal(false);
  protected readonly role = this.authService.role;

  private readonly canvas = viewChild<ElementRef<HTMLCanvasElement>>('chartCanvas');
  private chart: Chart | null = null;

  protected readonly indicators = this.indicatorsService.indicators;
  protected readonly selectedIndicatorId = signal<string>('');
  protected readonly summary = signal({ activeIndicators: 0, globalPendingTasks: 0 });
  protected readonly loading = this.analyticsService.loading;
  
  protected readonly activeTab = signal<'trends' | 'responses'>('trends');
  protected readonly allResponses = signal<any[]>([]);

  constructor() {
    // Re-render chart when selection changes
    effect(() => {
      const id = this.selectedIndicatorId();
      if (id) {
        this.updateChart(id);
      }
    });
  }

  async ngOnInit() {
    await this.indicatorsService.loadAll();

    // Default computed fallback from loaded indicators
    const allInds = this.indicators();
    let activeCount = allInds.filter(i => i.ativo).length;
    let pendingCount = 0;

    try {
      const stats: any = await this.analyticsService.getSystemSummary();
      if (stats) {
        const backendActive = stats.activeIndicators ?? stats.active_indicators ?? stats.activeIndicatorsCount ?? stats.activeCount;
        if (typeof backendActive === 'number' && backendActive > 0) {
          activeCount = backendActive;
        }

        const backendPending = stats.globalPendingTasks ?? stats.global_pending_tasks ?? stats.pendingTasks ?? stats.pendingCount;
        if (typeof backendPending === 'number') {
          pendingCount = backendPending;
        }
      }
    } catch (err) {
      console.warn('Endpoint /api/analytics/summary indisponível, usando fallback calculado.', err);
    }

    this.summary.set({
      activeIndicators: activeCount,
      globalPendingTasks: pendingCount
    });

    // Initial load for indicator trends
    const first = allInds[0];
    if (first) {
      this.selectedIndicatorId.set(first.id);
    }

    // Load all responses for the second tab
    try {
      const responses = await this.analyticsService.getAllResponses();
      this.allResponses.set(responses || []);
    } catch (err) {
      console.warn('Endpoint /api/analytics/responses indisponível', err);
    }
  }

  async exportXlsx() {
    try {
      await this.dataTransferService.exportToXlsx();
    } catch (err: any) {
      alert('Erro ao exportar base de dados: ' + (err.message || err));
    }
  }

  async updateChart(indicatorId: string) {
    const data = await this.analyticsService.getIndicatorTrend(indicatorId);
    const canvasEl = this.canvas()?.nativeElement;
    
    if (!canvasEl) return;

    if (this.chart) {
      this.chart.destroy();
    }

    const indicator = this.indicators().find(i => i.id === indicatorId);

    this.chart = new Chart(canvasEl, {
      type: 'line',
      data: {
        labels: data.map(p => p.label),
        datasets: [{
          label: indicator?.nome || 'Valor',
          data: data.map(p => p.value),
          borderColor: '#336290', // Secondary Institutional Blue
          backgroundColor: 'rgba(51, 98, 144, 0.12)',
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: '#336290',
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            mode: 'index',
            intersect: false,
            padding: 12,
            backgroundColor: 'rgba(0,0,0,0.8)',
            titleFont: { size: 14, weight: 'bold' },
            bodyFont: { size: 13 }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { color: 'rgba(0,0,0,0.05)' },
            title: { display: true, text: indicator?.unidade_medida || '' }
          },
          x: {
            grid: { display: false }
          }
        }
      }
    });
  }

  async logout() {
    await this.authService.logout();
  }
}
